import { Capacitor } from '@capacitor/core';
import type { IPurchaseStrategy, Platform } from './types';
import { WebPurchaseStrategy } from './WebPurchaseStrategy';
import { NativePurchaseStrategy } from './NativePurchaseStrategy';

// RevenueCat API Keys - Loaded from environment variables
// Use .env.local for sandbox keys and .env.production for production keys
// React Scripts requires VITE_ prefix for client-side environment variables
const REVENUECAT_API_KEY_IOS = import.meta.env.VITE_REVENUECAT_API_KEY_IOS;
const REVENUECAT_API_KEY_ANDROID = import.meta.env.VITE_REVENUECAT_API_KEY_ANDROID;
const REVENUECAT_API_KEY_WEB = import.meta.env.VITE_REVENUECAT_API_KEY_WEB;

export class PurchaseStrategyFactory {
  private static instance: IPurchaseStrategy | null = null;
  private static currentPlatform: Platform | null = null;

  static getCurrentPlatform(): Platform {
    const platform = Capacitor.getPlatform();

    switch (platform) {
      case 'ios':
        return 'ios';
      case 'android':
        return 'android';
      case 'web':
        return 'web';
      default:
        // Default to web for unknown platforms
        console.warn(`Unknown platform: ${platform}, defaulting to web`);
        return 'web';
    }
  }

  static getApiKeyForPlatform(platform: Platform): string {
    switch (platform) {
      case 'ios':
        if (!REVENUECAT_API_KEY_IOS) {
          throw new Error('VITE_REVENUECAT_API_KEY_IOS environment variable is required');
        }
        return REVENUECAT_API_KEY_IOS;

      case 'android':
        if (!REVENUECAT_API_KEY_ANDROID) {
          throw new Error('VITE_REVENUECAT_API_KEY_ANDROID environment variable is required');
        }
        return REVENUECAT_API_KEY_ANDROID;

      case 'web':
        if (!REVENUECAT_API_KEY_WEB) {
          throw new Error('VITE_REVENUECAT_API_KEY_WEB environment variable is required');
        }
        return REVENUECAT_API_KEY_WEB;

      default:
        throw new Error(`Unsupported platform: ${platform}`);
    }
  }

  static createStrategy(platform: Platform): IPurchaseStrategy {
    switch (platform) {
      case 'web':
        return new WebPurchaseStrategy();

      case 'ios':
      case 'android':
        return new NativePurchaseStrategy();

      default:
        throw new Error(`Unsupported platform: ${platform}`);
    }
  }

  static getInstance(): IPurchaseStrategy {
    const currentPlatform = PurchaseStrategyFactory.getCurrentPlatform();

    // If we don't have an instance or the platform changed, create a new one
    if (!PurchaseStrategyFactory.instance || PurchaseStrategyFactory.currentPlatform !== currentPlatform) {
      PurchaseStrategyFactory.instance = PurchaseStrategyFactory.createStrategy(currentPlatform);
      PurchaseStrategyFactory.currentPlatform = currentPlatform;
    }

    return PurchaseStrategyFactory.instance;
  }

  static async initializeStrategy(userId?: string, email?: string): Promise<IPurchaseStrategy> {
    const platform = PurchaseStrategyFactory.getCurrentPlatform();
    const apiKey = PurchaseStrategyFactory.getApiKeyForPlatform(platform);
    const strategy = PurchaseStrategyFactory.getInstance();

    await strategy.configure(apiKey, userId, email);

    return strategy;
  }

  // For testing purposes, allow resetting the singleton
  static reset(): void {
    PurchaseStrategyFactory.instance = null;
    PurchaseStrategyFactory.currentPlatform = null;
  }
}
